import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setUser, logout, User } from '../features/auth/authSlice';

// Admin email whitelist (HARD RULE)
const ADMIN_EMAILS = [
  'alimagdi12367@gmail.com',
  'youssefabdelmaged50@gmail.com',
  'admin@teo-eg.com',
] as const;

type AuthContextType = {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  login: (userData: User, token: string) => Promise<void>;
  logout: () => void;
  checkAdminAccess: (email: string) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user) as User | null;
  const [isLoading, setIsLoading] = useState(false);

  // Check if email is in admin whitelist
  const checkAdminAccess = (email: string): boolean => {
    return ADMIN_EMAILS.includes(email as typeof ADMIN_EMAILS[number]);
  };

  // Get admin status
  const isAdmin = user ? (user.role === 'admin' || checkAdminAccess(user.email)) : false;

  // Login function - validates email and stores token
  const login = async (userData: User, token: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Store token in localStorage
      localStorage.setItem('auth_token', token);

      // Add token to userData for Redux state
      const userWithToken = { ...userData, token };

      // Dispatch to Redux
      dispatch(setUser(userWithToken));
      
      // Store user in localStorage for persistence
      localStorage.setItem('auth_user', JSON.stringify(userWithToken));
    } catch (error: any) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const handleLogout = () => {
    dispatch(logout());
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  };

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        dispatch(setUser(userData));
      } catch (error) {
        handleLogout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const value: AuthContextType = {
    user,
    isAdmin,
    isLoading,
    login,
    logout: handleLogout,
    checkAdminAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

